import React, { useState, useEffect, useRef } from 'react';
import { getRestaurant, getPublicUser, getKnownCustomMeal, setRestaurantFavorite, setRestaurantBlocked, listenToPublicUser } from '../services/DataStore';
import { Restaurant } from '../models/Restaurant';
import { useAuth } from '../contexts/AuthContext';
import RestaurantView from './RestaurantView';

const UsersRestaurantList = ({ onClose }) => {
  const { currentUser, userData } = useAuth();
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [trashedRestaurants, setTrashedRestaurants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('favorites');
  const [hoveredRestaurant, setHoveredRestaurant] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [restaurantStatus, setRestaurantStatus] = useState(new Map()); // Map of restaurant ID to its statuses
  const hideTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const modalRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [restaurantToRemove, setRestaurantToRemove] = useState(null);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the confirmation dialog or hover popup
      if (showConfirmDialog || event.target.closest('.restaurant-popup')) return;
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose, showConfirmDialog]);

  // Add listener for public user updates
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = listenToPublicUser(currentUser.uid, async (publicUser) => {
      if (!publicUser) return;

      try {
        // Load favorite restaurants
        const favorites = await Promise.all(
          (publicUser.favoriterestaurants || []).map(async (id) => {
            try {
              return await getRestaurant(id);
            } catch (error) {
              console.error('Error loading favorite restaurant:', error);
              return null;
            }
          })
        );

        // Get trashed restaurants
        const trashedIds = new Set(publicUser.trashedrestaurants || []);
        
        // Get meal-blocked restaurants
        const mealBlocked = publicUser.getMealBlockedRestaurants();
        
        const mealBlockedIds = new Set();
        const statusMap = new Map();

        // Process meal-blocked restaurants
        Object.entries(mealBlocked).forEach(([mealId, restaurantIds]) => {
          const customMeal = userData?.getCustomMeal(mealId) || getKnownCustomMeal(mealId);
          
          if (customMeal) {
            restaurantIds.forEach(restaurantId => {
              mealBlockedIds.add(restaurantId);
              if (!statusMap.has(restaurantId)) {
                statusMap.set(restaurantId, new Set());
              }
              statusMap.get(restaurantId).add(mealId);
            });
          }
        });

        // Add trashed status to the map
        trashedIds.forEach(restaurantId => {
          if (!statusMap.has(restaurantId)) {
            statusMap.set(restaurantId, new Set());
          }
          statusMap.get(restaurantId).add('trashed');
        });

        // Get union of trashed and meal-blocked restaurants
        const allBlockedIds = new Set([...trashedIds, ...mealBlockedIds]);
        
        // Load all blocked restaurants
        const blocked = await Promise.all(
          Array.from(allBlockedIds).map(async (id) => {
            try {
              return await getRestaurant(id);
            } catch (error) {
              console.error('Error loading blocked restaurant:', error);
              return null;
            }
          })
        );

        // Sort blocked restaurants by name
        const sortedBlocked = blocked
          .filter(r => r !== null)
          .sort((a, b) => a.name.localeCompare(b.name));

        setFavoriteRestaurants(favorites.filter(r => r !== null));
        setTrashedRestaurants(sortedBlocked);
        setRestaurantStatus(statusMap);
        setIsLoading(false);
      } catch (error) {
        console.error('Error updating restaurants from public user:', error);
      }
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [currentUser, userData]);

  // Add scroll and wheel handlers
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set a new timeout to mark scrolling as finished
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);

      // Update hover state based on last known mouse position
      updateHoverState(lastMousePositionRef.current.x, lastMousePositionRef.current.y);
    };

    const handleWheel = (event) => {
      // Store the current mouse position
      lastMousePositionRef.current = {
        x: event.clientX,
        y: event.clientY
      };
    };

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      listElement.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll);
        listElement.removeEventListener('wheel', handleWheel);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Function to update hover state based on coordinates
  const updateHoverState = (mouseX, mouseY) => {
    if (!listRef.current) return;

    const restaurantElements = listRef.current.getElementsByClassName('restaurant-item');
    for (const element of restaurantElements) {
      const rect = element.getBoundingClientRect();
      if (mouseY >= rect.top && mouseY <= rect.bottom) {
        const restaurantId = element.getAttribute('data-restaurant-id');
        const restaurant = trashedRestaurants.find(r => r.id === restaurantId) || 
                         favoriteRestaurants.find(r => r.id === restaurantId);
        if (restaurant) {
          const listRect = listRef.current.getBoundingClientRect();
          
          // Calculate total height of popup (RestaurantView + Remove button)
          const popupHeight = 400 + 40; // 400px for RestaurantView + ~40px for button
          
          let x = listRect.right - 400;
          let y = rect.top;
          
          // Ensure popup doesn't go below viewport
          if (y + popupHeight > window.innerHeight) {
            y = window.innerHeight - popupHeight;
          }
          
          // Ensure popup doesn't go above viewport
          if (y < 0) {
            y = 0;
          }
          
          setPopupPosition({ x, y });
          setHoveredRestaurant(restaurant);
        }
        break;
      }
    }
  };

  const handleStatusClick = (restaurantId, status) => {
    console.log(`Clicked status for restaurant ${restaurantId}:`, status);
  };

  const renderStatusIcons = (restaurantId) => {
    const statuses = restaurantStatus.get(restaurantId);
    console.log(`Rendering status icons for restaurant ${restaurantId}:`, statuses ? Array.from(statuses) : 'no statuses');
    
    if (!statuses) return null;

    return (
      <div className="flex items-center space-x-1 mt-1">
        {Array.from(statuses).map(status => {
          console.log(`Processing status ${status} for restaurant ${restaurantId}`);
          if (status === 'trashed') {
            return (
              <button
                key="trashed"
                onClick={() => handleStatusClick(restaurantId, 'trashed')}
                className="p-1 rounded-full hover:bg-gray-100"
                title="Trashed"
              >
                <span className="material-symbols-outlined text-red-500 text-xl">delete</span>
              </button>
            );
          } else {
            // Check both user's custom meals and known meals
            const customMeal = userData?.getCustomMeal(status) || getKnownCustomMeal(status);
            console.log(`Meal ${status} - Found custom meal:`, customMeal ? 'Yes' : 'No');
            
            if (customMeal) {
              return (
                <button
                  key={status}
                  onClick={() => handleStatusClick(restaurantId, status)}
                  className="p-1 rounded-full hover:bg-gray-100"
                  title={`Blocked for ${customMeal.getDescription()}`}
                >
                  <span className="material-symbols-outlined text-accent text-xl">
                    {customMeal.getIconName()}
                  </span>
                </button>
              );
            }
          }
          return null;
        })}
      </div>
    );
  };

  const handleMouseEnter = (restaurant, event) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Store the current mouse position
    lastMousePositionRef.current = {
      x: event.clientX,
      y: event.clientY
    };

    const rect = event.currentTarget.getBoundingClientRect();
    const listRect = listRef.current.getBoundingClientRect();
    
    // Calculate total height of popup (RestaurantView + Remove button)
    const popupHeight = 400 + 40; // 400px for RestaurantView + ~40px for button
    
    let x = listRect.right - 400;
    let y = rect.top;
    
    // Ensure popup doesn't go below viewport
    if (y + popupHeight > window.innerHeight) {
      y = window.innerHeight - popupHeight;
    }
    
    // Ensure popup doesn't go above viewport
    if (y < 0) {
      y = 0;
    }
    
    setPopupPosition({ x, y });
    setHoveredRestaurant(restaurant);
  };

  const handleMouseLeave = () => {
    if (!isScrolling) {
      hideTimeoutRef.current = setTimeout(() => {
        setHoveredRestaurant(null);
      }, 300);
    }
  };

  const handleMouseMove = (event) => {
    // Store the current mouse position
    lastMousePositionRef.current = {
      x: event.clientX,
      y: event.clientY
    };

    if (isScrolling) {
      updateHoverState(event.clientX, event.clientY);
    }
  };

  const handleShowMap = (restaurant) => {
    if (restaurant.location?.coordinates) {
      const { latitude, longitude } = restaurant.location.coordinates;
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
    }
  };

  const handleRemove = async (restaurant) => {
    setRestaurantToRemove(restaurant);
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!restaurantToRemove) return;

    try {
      if (activeTab === 'favorites') {
        // Remove from favorites
        await setRestaurantFavorite(restaurantToRemove.id, false);
      } else {
        // Remove from all lists (trashed and meal-blocked)
        const statuses = restaurantStatus.get(restaurantToRemove.id);
        if (statuses) {
          // Remove from each meal block
          for (const status of statuses) {
            if (status !== 'trashed') {
              await setRestaurantBlocked(restaurantToRemove.id, false, { id: status });
            }
          }
          // Remove from trashed if it was there
          if (statuses.has('trashed')) {
            await setRestaurantBlocked(restaurantToRemove.id, false, null);
          }
        }
      }
    } catch (error) {
      console.error('Error removing restaurant:', error);
      alert('Failed to remove restaurant. Please try again.');
    } finally {
      setShowConfirmDialog(false);
      setRestaurantToRemove(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-2xl w-full h-[calc(100vh-200px)] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">My Restaurants</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'favorites'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('favorites')}
            >
              Favorites
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'trashed'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('trashed')}
            >
              Trashed
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4" ref={listRef}>
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'favorites' ? (
                favoriteRestaurants.length > 0 ? (
                  favoriteRestaurants.map(restaurant => (
                    <div
                      key={restaurant.id}
                      className="restaurant-item flex items-center space-x-4 p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                      data-restaurant-id={restaurant.id}
                      onMouseEnter={(e) => handleMouseEnter(restaurant, e)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        {restaurant.imageUrl ? (
                          <img
                            src={restaurant.imageUrl}
                            alt={restaurant.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('bg-gray-100');
                              e.target.parentElement.innerHTML = `
                                <span class="material-symbols-outlined text-gray-400 text-2xl">restaurant</span>
                              `;
                            }}
                          />
                        ) : (
                          <span className="material-symbols-outlined text-gray-400 text-2xl">restaurant</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {restaurant.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {restaurant.categories.map(cat => cat.title).join(', ')}
                        </p>
                        <div className="flex items-center mt-1">
                          <span className="text-yellow-400">★</span>
                          <span className="ml-1 text-sm text-gray-600">
                            {restaurant.rating} ({restaurant.reviewCount} reviews)
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No favorite restaurants yet
                  </div>
                )
              ) : (
                trashedRestaurants.length > 0 ? (
                  trashedRestaurants.map(restaurant => (
                    <div
                      key={restaurant.id}
                      className="restaurant-item flex items-center space-x-4 p-3 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                      data-restaurant-id={restaurant.id}
                      onMouseEnter={(e) => handleMouseEnter(restaurant, e)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        {restaurant.imageUrl ? (
                          <img
                            src={restaurant.imageUrl}
                            alt={restaurant.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('bg-gray-100');
                              e.target.parentElement.innerHTML = `
                                <span class="material-symbols-outlined text-gray-400 text-2xl">restaurant</span>
                              `;
                            }}
                          />
                        ) : (
                          <span className="material-symbols-outlined text-gray-400 text-2xl">restaurant</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate mb-0.5">
                          {restaurant.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {restaurant.categories.map(cat => cat.title).join(', ')}
                        </p>
                        <div className="flex items-center mt-0.5">
                          <span className="text-yellow-400">★</span>
                          <span className="ml-1 text-sm text-gray-600">
                            {restaurant.rating} ({restaurant.reviewCount} reviews)
                          </span>
                        </div>
                        {renderStatusIcons(restaurant.id)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No trashed restaurants
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hover Popup */}
      {hoveredRestaurant && (
        <div 
          className="fixed z-[70] restaurant-popup"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`
          }}
          onMouseEnter={() => {
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleMouseLeave}
        >
          <div className="inline-block">
            <RestaurantView 
              restaurant={hoveredRestaurant}
              onShowMap={handleShowMap}
            />
            <button
              onClick={() => handleRemove(hoveredRestaurant)}
              className="w-full bg-red-500 text-white px-4 py-2 rounded-b-lg hover:bg-red-600 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {activeTab === 'favorites' 
                ? `Remove ${restaurantToRemove?.name} from favorites?`
                : `Remove ${restaurantToRemove?.name} from trashed?`}
            </h3>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersRestaurantList; 